// client/src/components/plataforma/franquias/FranquiaFormPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { useFranquiaById, useMutateFranquias } from '../../../hooks/useFranquias';
import { useFranquiaPlans } from '../../../hooks/useFranquiaPlans';
import { useFranquiaAdmins, useFranquiaAdminMutations } from '../../../hooks/useUsers';
import { Loader2, Save, Building, User, DollarSign, UserCheck, Globe, Check, X, Trash2, UserPlus } from 'lucide-react';
import SearchableSelect from '../../shared/SearchableSelect';
import useAlert from '../../../hooks/useAlert';
import PageHeader from '../../layout/PageHeader';
import FabSaveButton from '../../common/FabSaveButton';
import api from '../../../services/api';

// Domínio público das páginas de franquia. Igual ao PUBLIC_CONSUMER_DOMAIN do backend.
const DOMINIO_PAGINA = process.env.REACT_APP_SITE_CONTA_URL
    ? process.env.REACT_APP_SITE_CONTA_URL.replace(/^https?:\/\//, '')
    : 'situai.site';

// Slugify no cliente — espelha utils/slugify.js do servidor (o servidor é a
// fonte da verdade; isto é só pra sugestão e feedback ao vivo).
const clientSlugify = (str) => String(str || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '');

// Sugestão de endereço: franquia "regional" (nome ≈ cidade) → "{cidade}-{uf}";
// franquia nomeada (marca/pessoa) → "{nome}-{cidade}-{uf}".
const sugerirSlug = (nome, cidade, estado) => {
    const n = clientSlugify(nome);
    const c = clientSlugify(cidade);
    const uf = clientSlugify(estado);
    if (!c || !uf) return '';
    const regional = !n || n === c || c.includes(n) || n.includes(c);
    return regional ? `${c}-${uf}` : `${n}-${c}-${uf}`;
};

const formatCPF = (value) => value.replace(/\D/g, '').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d{1,2})$/, '$1-$2').substring(0, 14);
const formatCNPJ = (value) => value.replace(/\D/g, '').replace(/(\d{2})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1/$2').replace(/(\d{4})(\d)/, '$1-$2').substring(0, 18);
const formatTelefone = (value) => { let v = value.replace(/\D/g, '').substring(0, 11); if (v.length > 10) v = v.replace(/^(\d{2})(\d{5})(\d{4})/, '($1) $2-$3'); else v = v.replace(/^(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3'); return v; };

const fetchEstados = async () => (await axios.get('https://servicodados.ibge.gov.br/api/v1/localidades/estados?orderBy=nome')).data;
const fetchCidades = async (uf) => (await axios.get(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/${uf}/municipios?orderBy=nome`)).data;
const fetchBancos = async () => {
    const { data } = await axios.get('https://brasilapi.com.br/api/banks/v1');
    return data.filter(b => b.code).map(b => ({ value: b.code.toString(), label: `${b.code} - ${b.name}` }));
};

const FranquiaFormPage = () => {
    const { id: franquiaId } = useParams();
    const navigate = useNavigate();
    const { setAlert } = useAlert();
    const isEditing = !!franquiaId;

    const { data: franquiaData, isLoading: pageLoading } = useFranquiaById(franquiaId);
    const { createFranquia, updateFranquia, isPending: isMutationLoading } = useMutateFranquias();
    const { data: franchisePlans = [], isLoading: plansLoading } = useFranquiaPlans({ status: 'active' });
    const { data: estados = [], isLoading: loadingEstados } = useQuery({ queryKey: ['estadosIBGE'], queryFn: fetchEstados });
    const { data: bancos = [], isLoading: loadingBancos } = useQuery({ queryKey: ['bancosBrasilAPI'], queryFn: fetchBancos });
    
    // ✅ CORREÇÃO: Usando useFranquiaAdmins em vez de useUsersByFranquia
    const { data: administradores = [], isLoading: loadingUsuarios } = useFranquiaAdmins(franquiaId);
    const { addAdmin, removeAdmin, isPending: adminMutating } = useFranquiaAdminMutations(franquiaId);

    // Form embutido pra adicionar um novo administrador (só no modo edição).
    const [novoAdmin, setNovoAdmin] = useState({ name: '', email: '', telefone: '', cpf: '', password: '' });
    const [mostrarNovoAdmin, setMostrarNovoAdmin] = useState(false);
    
    const [activeTab, setActiveTab] = useState('info');
    const [formData, setFormData] = useState({
        nome: '', slug: '', cidade: '', estado: '', emailContato: '', telefoneContato: '', plano: '',
        tipoPessoa: 'juridica', documento: '',
        admin: { name: '', email: '', password: '', telefone: '', cpf: '' },
        banco: { bank_code: '', agencia: '', agencia_dv: '', conta: '', conta_dv: '', nome_titular: '', tipo_conta: 'checking', holder_type: 'individual', holder_document: '' },
        responsavel: ''
    });
    // Endereço público ({slug}.situai.site): enquanto o admin não digitar nada,
    // sugerimos automaticamente a partir de nome/cidade/UF.
    const [slugEditadoManualmente, setSlugEditadoManualmente] = useState(isEditing);
    const [slugStatus, setSlugStatus] = useState({ checking: false, available: null, reason: null, normalized: '' });
    
    const { data: cidades = [], isLoading: loadingCidades } = useQuery({
        queryKey: ['cidadesIBGE', formData.estado],
        queryFn: () => fetchCidades(formData.estado),
        enabled: !!formData.estado,
    });

    useEffect(() => {
        if (isEditing && franquiaData) {
            setFormData(prev => ({
                ...prev,
                nome: franquiaData.nome || '',
                slug: franquiaData.slug || '',
                cidade: franquiaData.cidade || '',
                estado: franquiaData.estado || '',
                emailContato: franquiaData.emailContato || '',
                telefoneContato: formatTelefone(franquiaData.telefoneContato || ''),
                plano: franquiaData.plano?._id || franquiaData.plano || '',
                tipoPessoa: franquiaData.tipoPessoa || 'juridica',
                documento: franquiaData.documento ? (franquiaData.tipoPessoa === 'fisica' ? formatCPF(franquiaData.documento) : formatCNPJ(franquiaData.documento)) : '',
                admin: {
                    ...prev.admin,
                    name: franquiaData.responsavel?.name || '',
                    email: franquiaData.responsavel?.email || '',
                    telefone: formatTelefone(franquiaData.responsavel?.telefone || ''),
                    cpf: formatCPF(franquiaData.responsavel?.cpf || '')
                },
                banco: {
                    ...prev.banco,
                    ...(franquiaData.banco || {})
                },
                responsavel: franquiaData.responsavel?._id || ''
            }));
        }
    }, [isEditing, franquiaData]);

    // Sugestão automática do endereço enquanto o admin não editar o campo à mão.
    useEffect(() => {
        if (slugEditadoManualmente) return;
        const sugestao = sugerirSlug(formData.nome, formData.cidade, formData.estado);
        setFormData(prev => (prev.slug === sugestao ? prev : { ...prev, slug: sugestao }));
    }, [formData.nome, formData.cidade, formData.estado, slugEditadoManualmente]);

    // Checagem de disponibilidade (debounce 450ms) contra o backend.
    useEffect(() => {
        const slug = (formData.slug || '').trim();
        if (!slug) {
            setSlugStatus({ checking: false, available: null, reason: null, normalized: '' });
            return;
        }
        if (isEditing && slug === (franquiaData?.slug || '')) {
            setSlugStatus({ checking: false, available: true, reason: null, normalized: slug });
            return;
        }
        setSlugStatus(s => ({ ...s, checking: true }));
        const t = setTimeout(async () => {
            try {
                const { data } = await api.get('/franquias/slug-disponivel', {
                    params: { slug, ...(franquiaId ? { id: franquiaId } : {}) }
                });
                setSlugStatus({ checking: false, ...data.data });
            } catch (err) {
                setSlugStatus({ checking: false, available: null, reason: null, normalized: slug });
            }
        }, 450);
        return () => clearTimeout(t);
    }, [formData.slug, franquiaId, isEditing, franquiaData]);

    const handleChange = useCallback((e) => {
        let { name, value } = e.target;

        if (name === 'slug') {
            setSlugEditadoManualmente(true);
            value = clientSlugify(value.replace(/\s+/g, '-'));
            setFormData(prev => ({ ...prev, slug: value }));
            return;
        }

        if (name === 'documento') value = formData.tipoPessoa === 'fisica' ? formatCPF(value) : formatCNPJ(value);
        if (name.startsWith('admin.')) {
            const adminField = name.split('.')[1];
            let formattedValue = value;
            if (adminField === 'cpf') formattedValue = formatCPF(value);
            if (adminField === 'telefone') formattedValue = formatTelefone(value);
            setFormData(prev => ({ ...prev, admin: { ...prev.admin, [adminField]: formattedValue } }));
        } else if (name.startsWith('banco.')) {
            const bancoField = name.split('.')[1];
            let formattedBancoValue = value;
            if (bancoField === 'holder_document') formattedBancoValue = formData.banco.holder_type === 'individual' ? formatCPF(value) : formatCNPJ(value);
            setFormData(prev => ({ ...prev, banco: { ...prev.banco, [bancoField]: formattedBancoValue } }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    }, [formData.tipoPessoa, formData.banco.holder_type]);

    const handleResponsavelChange = (userId) => {
        setFormData(prev => ({ ...prev, responsavel: userId }));
    };

    const handleNovoAdminChange = (e) => {
        let { name, value } = e.target;
        if (name === 'cpf') value = formatCPF(value);
        if (name === 'telefone') value = formatTelefone(value);
        setNovoAdmin(prev => ({ ...prev, [name]: value }));
    };

    const handleAddAdmin = () => {
        if (!novoAdmin.name || !novoAdmin.email || !novoAdmin.password) {
            setAlert('Preencha nome, e-mail e senha do novo administrador.', 'warning');
            return;
        }
        addAdmin.mutate(
            {
                ...novoAdmin,
                cpf: novoAdmin.cpf.replace(/\D/g, ''),
                telefone: novoAdmin.telefone.replace(/\D/g, ''),
            },
            {
                onSuccess: () => {
                    setNovoAdmin({ name: '', email: '', telefone: '', cpf: '', password: '' });
                    setMostrarNovoAdmin(false);
                },
            }
        );
    };

    const handleRemoveAdmin = (admin) => {
        if (!window.confirm(`Remover "${admin.name}" da franquia?`)) return;
        removeAdmin.mutate(admin.vinculoId);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if ((formData.slug || '').trim() && slugStatus.available === false) {
            setActiveTab('info');
            const motivos = {
                invalid: 'O endereço da página é inválido.',
                reserved: 'Esse endereço é reservado pelo sistema.',
                taken: 'Esse endereço já está em uso por outra página.',
            };
            setAlert(motivos[slugStatus.reason] || 'Escolha outro endereço para a página.', 'warning');
            return;
        }

        const dataToSend = {
            ...formData,
            slug: (formData.slug || '').trim(),
            documento: formData.documento.replace(/\D/g, ''),
            telefoneContato: formData.telefoneContato.replace(/\D/g, ''),
            admin: {
                ...formData.admin,
                cpf: formData.admin.cpf.replace(/\D/g, ''),
                telefone: formData.admin.telefone.replace(/\D/g, '')
            },
            banco: formData.banco.holder_document ? {
                ...formData.banco,
                holder_document: formData.banco.holder_document.replace(/\D/g, '')
            } : undefined
        };
        
        const options = { onSuccess: () => navigate('/franquias') };

        if (isEditing) {
            delete dataToSend.admin;
            updateFranquia.mutate({ id: franquiaId, franquiaData: dataToSend }, options);
        } else {
            if (!dataToSend.plano) {
                setAlert('Por favor, selecione um plano para a nova franquia.', 'warning');
                return;
            }
            createFranquia.mutate(dataToSend, options);
        }
    };
    
    const isLoading = isMutationLoading || pageLoading || loadingEstados || loadingBancos || loadingUsuarios;

    return (
        <div className="page-management-container">
            <form onSubmit={handleSubmit}>
                {/* Antes montava o cabecalho na mao — os icones do "Voltar" e do
                    "Salvar" tambem nao tinham className="icon", entao ficavam
                    colados no texto (action-button-base so da o gap via essa
                    classe). Convertido pra PageHeader (navBack + actions),
                    que ja resolve os dois problemas de uma vez. */}
                <PageHeader
                    icon={<Building />}
                    title={isEditing ? `Editar Franquia: ${formData.nome}` : 'Nova Franquia'}
                    navBack={{ to: '/franquias', text: 'Voltar' }}
                />
                <div className="tabs-nav mt-6">
                    <button
                        type="button"
                        className={`tab-button ${activeTab === 'info' ? 'is-active' : ''}`}
                        onClick={() => setActiveTab('info')}
                    >
                        <Building size={16} /> Informações da Franquia
                    </button>
                    <button
                        type="button"
                        className={`tab-button ${activeTab === 'admin' ? 'is-active' : ''}`}
                        onClick={() => setActiveTab('admin')}
                    >
                        <User size={16} /> Admin da Franquia
                    </button>
                    <button
                        type="button"
                        className={`tab-button ${activeTab === 'banco' ? 'is-active' : ''}`}
                        onClick={() => setActiveTab('banco')}
                    >
                        <DollarSign size={16} /> Dados Bancários
                    </button>
                </div>
                <div className="content-panel mt-0 pt-8">
                    {activeTab === 'info' && (
                        <fieldset className="form-section" disabled={isLoading}>
                            <legend className="form-section-title">Dados Gerais e de Contato</legend>
                            <div className="form-grid-container grid-cols-1 md:grid-cols-2">
                                <div className="form-group">
                                    <label>Nome da Franquia*</label>
                                    <input
                                        type="text"
                                        name="nome"
                                        value={formData.nome}
                                        onChange={handleChange}
                                        className="form-input"
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Plano da Franquia*</label>
                                    <select
                                        name="plano"
                                        value={formData.plano}
                                        onChange={handleChange}
                                        className="form-input"
                                        required
                                        disabled={plansLoading}
                                    >
                                        <option value="">
                                            {plansLoading ? 'A carregar...' : 'Selecione um plano'}
                                        </option>
                                        {franchisePlans.map((plan) => (
                                            <option key={plan._id} value={plan._id}>
                                                {plan.name} - {(plan.price/100).toLocaleString('pt-BR', {style:'currency', currency:'BRL'})}/mês
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Tipo de Pessoa*</label>
                                    <select
                                        name="tipoPessoa"
                                        value={formData.tipoPessoa}
                                        onChange={handleChange}
                                        className="form-input"
                                        required
                                    >
                                        <option value="juridica">Pessoa Jurídica</option>
                                        <option value="fisica">Pessoa Física</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Documento (CPF/CNPJ da Franquia)*</label>
                                    <input
                                        type="text"
                                        name="documento"
                                        value={formData.documento}
                                        onChange={handleChange}
                                        className="form-input"
                                        required
                                        placeholder={formData.tipoPessoa === 'fisica' ? '000.000.000-00' : '00.000.000/0000-00'}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Estado (UF)*</label>
                                    <select
                                        name="estado"
                                        value={formData.estado}
                                        onChange={handleChange}
                                        className="form-input"
                                        required
                                        disabled={loadingEstados}
                                    >
                                        <option value="">
                                            {loadingEstados ? 'A carregar...' : 'Selecione'}
                                        </option>
                                        {estados.map((e) => (
                                            <option key={e.id} value={e.sigla}>
                                                {e.nome}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Cidade*</label>
                                    <select
                                        name="cidade"
                                        value={formData.cidade}
                                        onChange={handleChange}
                                        className="form-input"
                                        required
                                        disabled={!formData.estado || loadingCidades}
                                    >
                                        <option value="">
                                            {loadingCidades ? 'A carregar...' : 'Selecione'}
                                        </option>
                                        {cidades.map((c) => (
                                            <option key={c.id} value={c.nome}>
                                                {c.nome}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group full-width md:col-span-2">
                                    <label>Endereço da Página Pública</label>
                                    <input
                                        type="text"
                                        name="slug"
                                        value={formData.slug}
                                        onChange={handleChange}
                                        className="form-input"
                                        placeholder="ex.: varginha-mg"
                                        autoComplete="off"
                                        spellCheck={false}
                                    />
                                    <p className="form-hint" style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                        <Globe size={14} className="icon" />
                                        <span>
                                            {formData.slug
                                                ? <><strong>{formData.slug}.{DOMINIO_PAGINA}</strong></>
                                                : <>Deixe em branco para gerar automaticamente de cidade/UF.</>}
                                        </span>
                                        {formData.slug && slugStatus.checking && (
                                            <span style={{ color: 'var(--color-text-light)' }}>· verificando…</span>
                                        )}
                                        {formData.slug && !slugStatus.checking && slugStatus.available === true && (
                                            <span style={{ color: 'var(--color-success, #16a34a)', display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                                                <Check size={14} /> disponível
                                            </span>
                                        )}
                                        {formData.slug && !slugStatus.checking && slugStatus.available === false && (
                                            <span style={{ color: 'var(--color-danger, #E53E3E)', display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                                                <X size={14} />
                                                {slugStatus.reason === 'reserved' ? 'reservado pelo sistema'
                                                    : slugStatus.reason === 'taken' ? 'já está em uso'
                                                    : 'inválido'}
                                            </span>
                                        )}
                                    </p>
                                </div>
                                <div className="form-group full-width md:col-span-2">
                                    <label>Email de Contato*</label>
                                    <input
                                        type="email"
                                        name="emailContato"
                                        value={formData.emailContato}
                                        onChange={handleChange}
                                        className="form-input"
                                        required
                                    />
                                </div>
                                <div className="form-group full-width md:col-span-2">
                                    <label>Telefone de Contato*</label>
                                    <input
                                        type="tel"
                                        name="telefoneContato"
                                        value={formData.telefoneContato}
                                        onChange={handleChange}
                                        className="form-input"
                                        required
                                    />
                                </div>
                            </div>
                        </fieldset>
                    )}
                    {activeTab === 'admin' && (
                        <fieldset className="form-section" disabled={isLoading}>
                            <legend className="form-section-title">Administradores da Franquia</legend>
                            
                            {isEditing ? (
                                <div>
                                    <div className="form-group">
                                        <label>Responsável pela Franquia</label>
                                        {loadingUsuarios ? (
                                            <div className="flex items-center text-gray-500">
                                                <Loader2 size={16} className="animate-spin mr-2" />
                                                Carregando administradores...
                                            </div>
                                        ) : administradores.length === 0 ? (
                                            <p className="form-hint">
                                                Nenhum administrador cadastrado nesta franquia. Adicione um abaixo.
                                            </p>
                                        ) : (
                                            <select
                                                value={formData.responsavel}
                                                onChange={(e) => handleResponsavelChange(e.target.value)}
                                                className="form-input"
                                            >
                                                <option value="">Selecione um responsável</option>
                                                {administradores.map(admin => (
                                                    <option key={admin._id} value={admin._id}>
                                                        {admin.name} — {admin.email}
                                                    </option>
                                                ))}
                                            </select>
                                        )}
                                        <p className="form-hint">O responsável recebe os avisos e representa a franquia. Salve o formulário para aplicar a troca.</p>
                                    </div>

                                    <div className="mt-6">
                                        <div className="flex items-center justify-between mb-4">
                                            <h3 className="text-lg font-medium">Administradores ({administradores.length})</h3>
                                            <button
                                                type="button"
                                                className="action-button action-button-secondary"
                                                onClick={() => setMostrarNovoAdmin(v => !v)}
                                            >
                                                <UserPlus size={18} className="icon" />
                                                {mostrarNovoAdmin ? 'Cancelar' : 'Adicionar administrador'}
                                            </button>
                                        </div>

                                        {mostrarNovoAdmin && (
                                            <div className="content-panel mb-4">
                                                <div className="form-grid-container grid-cols-1 md:grid-cols-2">
                                                    <div className="form-group">
                                                        <label>Nome*</label>
                                                        <input type="text" name="name" value={novoAdmin.name} onChange={handleNovoAdminChange} className="form-input" />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>E-mail*</label>
                                                        <input type="email" name="email" value={novoAdmin.email} onChange={handleNovoAdminChange} className="form-input" />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Telefone</label>
                                                        <input type="tel" name="telefone" value={novoAdmin.telefone} onChange={handleNovoAdminChange} className="form-input" placeholder="(XX) XXXXX-XXXX" />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>CPF</label>
                                                        <input type="text" name="cpf" value={novoAdmin.cpf} onChange={handleNovoAdminChange} className="form-input" placeholder="000.000.000-00" />
                                                    </div>
                                                    <div className="form-group full-width md:col-span-2">
                                                        <label>Senha inicial*</label>
                                                        <input type="password" name="password" value={novoAdmin.password} onChange={handleNovoAdminChange} className="form-input" autoComplete="new-password" />
                                                    </div>
                                                </div>
                                                <div className="form-actions">
                                                    <button type="button" className="action-button action-button-primary" onClick={handleAddAdmin} disabled={adminMutating}>
                                                        {adminMutating ? <Loader2 size={18} className="icon animate-spin" /> : <UserPlus size={18} className="icon" />}
                                                        Adicionar
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        <div className="table-container">
                                        <div className="table-wrapper">
                                            <table className="styled-table">
                                                <thead>
                                                    <tr>
                                                        <th>Nome</th>
                                                        <th>Email</th>
                                                        <th>Papel</th>
                                                        <th>Status</th>
                                                        <th></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {administradores.length === 0 ? (
                                                        <tr><td colSpan={5} className="text-center text-gray-500 py-4">Nenhum administrador nesta franquia.</td></tr>
                                                    ) : administradores.map(admin => (
                                                        <tr key={admin._id} className={admin._id === formData.responsavel ? 'bg-blue-50' : ''}>
                                                            <td className="td-name">
                                                                {admin.name}
                                                                {admin._id === formData.responsavel && (
                                                                    <span className="ml-2 badge badge-success">Responsável</span>
                                                                )}
                                                            </td>
                                                            <td>{admin.email}</td>
                                                            <td>
                                                                <span className={`badge ${admin.role?.scope === 'matriz' ? 'badge-danger' : 'badge-purple'}`}>
                                                                    {admin.role?.name || (admin.role?.scope === 'matriz' ? 'Matriz' : 'Franquia')}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <span className={`badge ${admin.isActive ? 'badge-success' : 'badge-danger'}`}>
                                                                    {admin.isActive ? 'Ativo' : 'Inativo'}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <button
                                                                    type="button"
                                                                    className="action-button-icon text-red-600 hover:text-red-800"
                                                                    title={admin._id === formData.responsavel ? 'Defina outro responsável antes de remover' : 'Remover administrador'}
                                                                    onClick={() => handleRemoveAdmin(admin)}
                                                                    disabled={adminMutating || admin._id === formData.responsavel || administradores.length <= 1}
                                                                >
                                                                    <Trash2 size={18} className="icon" />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="form-grid-container grid-cols-1 md:grid-cols-2">
                                    <div className="form-group">
                                        <label>Nome do Admin*</label>
                                        <input
                                            type="text"
                                            name="admin.name"
                                            value={formData.admin.name}
                                            onChange={handleChange}
                                            className="form-input"
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Email do Admin*</label>
                                        <input
                                            type="email"
                                            name="admin.email"
                                            value={formData.admin.email}
                                            onChange={handleChange}
                                            className="form-input"
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Telefone*</label>
                                        <input
                                            type="tel"
                                            name="admin.telefone"
                                            value={formData.admin.telefone}
                                            onChange={handleChange}
                                            className="form-input"
                                            placeholder="(XX) XXXXX-XXXX"
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>CPF do Admin*</label>
                                        <input
                                            type="text"
                                            name="admin.cpf"
                                            value={formData.admin.cpf}
                                            onChange={handleChange}
                                            className="form-input"
                                            placeholder="000.000.000-00"
                                            required
                                        />
                                    </div>
                                    <div className="form-group full-width">
                                        <label>Senha Inicial*</label>
                                        <input
                                            type="password"
                                            name="admin.password"
                                            value={formData.admin.password}
                                            onChange={handleChange}
                                            className="form-input"
                                            required
                                        />
                                    </div>
                                </div>
                            )}
                        </fieldset>
                    )}
                    {activeTab === 'banco' && (
                        <fieldset className="form-section" disabled={isLoading}>
                            <legend className="form-section-title">Dados Bancários para Repasse</legend>
                            <p className="form-hint mb-4">Preencha ou atualize os dados bancários.</p>
                            <div className="form-grid-container grid-cols-1 md:grid-cols-4">
                                <div className="form-group md:col-span-2">
                                    <label>Nome do Titular*</label>
                                    <input
                                        type="text"
                                        id="nome_titular"
                                        name="banco.nome_titular"
                                        value={formData.banco.nome_titular}
                                        onChange={handleChange}
                                        className="form-input"
                                        required
                                        maxLength="30"
                                    />
                                    <p className="form-hint text-right">
                                        {formData.banco.nome_titular.length}/30
                                    </p>
                                </div>
                                <div className="form-group">
                                    <label>Tipo de Titular*</label>
                                    <select
                                        name="banco.holder_type"
                                        value={formData.banco.holder_type}
                                        onChange={handleChange}
                                        className="form-input"
                                        required
                                    >
                                        <option value="individual">Pessoa Física</option>
                                        <option value="company">Pessoa Jurídica</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>CPF/CNPJ do Titular*</label>
                                    <input
                                        type="text"
                                        name="banco.holder_document"
                                        value={formData.banco.holder_document}
                                        onChange={handleChange}
                                        className="form-input"
                                        required
                                        placeholder={formData.banco.holder_type === 'individual' ? 'CPF' : 'CNPJ'}
                                    />
                                </div>
                                <div className="form-group md:col-span-2">
                                    <label>Banco*</label>
                                    <SearchableSelect
                                        name="banco.bank_code"
                                        options={bancos}
                                        value={formData.banco.bank_code}
                                        onChange={handleChange}
                                        placeholder="Buscar banco..."
                                    />
                                </div>
                                <div className="form-group md:col-span-2">
                                    <label>Tipo de Conta*</label>
                                    <select
                                        name="banco.tipo_conta"
                                        value={formData.banco.tipo_conta}
                                        onChange={handleChange}
                                        className="form-input"
                                        required
                                    >
                                        <option value="checking">Conta Corrente</option>
                                        <option value="savings">Conta Poupança</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Agência*</label>
                                    <input
                                        type="text"
                                        name="banco.agencia"
                                        value={formData.banco.agencia}
                                        onChange={handleChange}
                                        className="form-input"
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Dígito Agência</label>
                                    <input
                                        type="text"
                                        name="banco.agencia_dv"
                                        value={formData.banco.agencia_dv}
                                        onChange={handleChange}
                                        className="form-input"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Conta*</label>
                                    <input
                                        type="text"
                                        name="banco.conta"
                                        value={formData.banco.conta}
                                        onChange={handleChange}
                                        className="form-input"
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Dígito da Conta*</label>
                                    <input
                                        type="text"
                                        name="banco.conta_dv"
                                        value={formData.banco.conta_dv}
                                        onChange={handleChange}
                                        className="form-input"
                                        required
                                    />
                                </div>
                            </div>
                        </fieldset>
                    )}
                </div>
                <FabSaveButton loading={isLoading} label="Salvar" />
            </form>
        </div>
    );
};

export default FranquiaFormPage;